import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const PeelAndStickForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  
  // Define all the form fields based on the image
  const [formData, setFormData] = useState({
    projectName: '',
    jobAddress: '',
    jobNumber: '',
    date: '',
    items: {
      nailBase: { quantity: '', notes: '' },
      midPlyly: { quantity: '', notes: '' },
      cap: { quantity: '', notes: '' },
      dripEdge2x4: { quantity: '', notes: '' },
      sprayerPrimer: { quantity: '', notes: '' },
      mastic: { quantity: '', notes: '' },
      granulesColor: { quantity: '', notes: '' },
      flashings: { quantity: '', notes: '' },
      caulkingAndColor: { quantity: '', notes: '' },
      sprayPaintAndColor: { quantity: '', notes: '' },
      capNails: { quantity: '', notes: '' },
      oneInchFlashing: { quantity: '', notes: '' },
      twoInchFlashing: { quantity: '', notes: '' },
      threeInchFlashing: { quantity: '', notes: '' },
      largeTTop: { quantity: '', notes: '' },
      smallTTop: { quantity: '', notes: '' },
      oval: { quantity: '', notes: '' }
    }
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'peelAndStick') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          const projectData = await fetchProjectData(projectId);
          const prefilled = prefillFormData(projectData, formData);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (itemKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemKey]: {
          ...prev.items[itemKey],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Peel and Stick form updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'peelAndStick',
          formData
        })).unwrap();
        showToast('Peel and Stick form saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Peel and Stick form', 'error');
    } finally {
      setLoading(false);
    }
  };
  

  const handleDownload = () => {
    // Create a printable version of the form content
    let tableRows = '';
    Object.entries(formData.items).forEach(([key, item]) => {
      tableRows += formatTableRow(formatItemLabel(key), item.quantity);
    });
    
    const formContent = `
      ${createCompanyHeader()}
      <h1 class="form-title">Peel and Stick</h1>
      
      <div class="form-section">
        ${formatFormField('Project Name', formData.projectName)}
        ${formatFormField('Job Address', formData.jobAddress)}
        ${formatFormField('Job Number', formData.jobNumber)}
        ${formatFormField('Date', formData.date)}
      </div>
      
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Quantity</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    `;
    
    // Print the form
    printPermitForm(formData, 'Peel and Stick', formContent);
  };

  // Helper function to format camelCase keys into readable labels
  const formatItemLabel = (key) => {
    // Special cases
    if (key === 'nailBase') return 'Nail Base';
    if (key === 'midPlyly') return 'Mid Plyly';
    if (key === 'cap') return 'Cap';
    if (key === 'dripEdge2x4') return 'Drip Edge 2x4"';
    if (key === 'sprayerPrimer') return 'Sprayer Primer';
    if (key === 'mastic') return 'Mastic';
    if (key === 'granulesColor') return 'Granules (color)';
    if (key === 'flashings') return 'Flashing(s)';
    if (key === 'caulkingAndColor') return 'Caulking and color';
    if (key === 'sprayPaintAndColor') return 'Spray paint and color';
    if (key === 'capNails') return 'Cap Nails';
    if (key === 'oneInchFlashing') return '1" flashing';
    if (key === 'twoInchFlashing') return '2" flashing';
    if (key === 'threeInchFlashing') return '3" flashing';
    if (key === 'largeTTop') return 'Large T top';
    if (key === 'smallTTop') return 'Small T top';
    if (key === 'oval') return 'Oval';
    
    // General case: convert camelCase to spaces and capitalize
    const withSpaces = key.replace(/([A-Z])/g, ' $1').trim();
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img 
            src="/logo/topsail-logo-small.png" 
            alt="Topsail roofing" 
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">Topsail roofing</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>205 E Avenida Magdalena</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 370-8612</p>
          <p className="uppercase">topsailroofing.com</p>
          <p>CA 1070037</p>
        </div>
      </div>

      <h1 className="text-2xl font-bold mb-6 text-center">Peel and Stick</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name:</label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => handleChange('projectName', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Address:</label>
            <input
              type="text"
              value={formData.jobAddress}
              onChange={(e) => handleChange('jobAddress', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date:</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Number:</label>
            <input
              type="text"
              value={formData.jobNumber}
              onChange={(e) => handleChange('jobNumber', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
        </div>

        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr>
                <th className="border border-gray-300 p-2 w-1/2 text-left">Item</th>
                <th className="border border-gray-300 p-2 w-1/2 text-center">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(formData.items).map(([key, value]) => (
                <tr key={key}>
                  <td className="border border-gray-300 p-2 font-medium">
                    {formatItemLabel(key)}
                  </td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="text"
                      value={value.quantity}
                      onChange={(e) => handleItemChange(key, 'quantity', e.target.value)}
                      className="w-full p-1 border border-gray-200 rounded"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default PeelAndStickForm;
